import React from 'react';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import BaseButton from 'components/atoms/baseButton';
import { useFormik } from 'formik';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import BaseTextArea from 'components/atoms/base-textarea';
import { useUpdateUnregisteredConversationNotes } from 'infrastructure/hooks/smsDashboard/useUpdateUnregisteredConversationNotes';
import useAlert from 'infrastructure/hooks/utils/use-alert';

export interface IEditNoteModalProps {
  isOpen: boolean;
  toggle: () => void;
  note: string | null;
  conversationId: number;
  noteGuid: string;
}

const EditNoteModal: React.FC<IEditNoteModalProps> = (props) => {
  const { isOpen, toggle, note, noteGuid, conversationId } = props;
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();

  const {
    updateUnregisteredConversationNote,
    updateUnregisteredConversationNoteLoading,
  } = useUpdateUnregisteredConversationNotes({
    onSuccess: () => {
      resetFormikValues();
      toggle();
      notificationController.success({
        message: t('messages.updateSuccess', {
          value: t('labels.note'),
        }),
      });
    },
  });

  const modalName = t(`smsDashboard.edit_note`);

  const formik = useFormik({
    initialValues: {
      note: note || '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      updateUnregisteredConversationNote({
        conversationId,
        noteGuid,
        note: values.note as string,
      });
    },
  });
  const { submitForm, setValues, values } = formik;

  const resetFormikValues = (customName?: any) => {
    setValues({
      note: customName || '',
    });
  };

  const onCancel = async () => {
    const answer = await showAlert({
      title: t('confirm.title'),
      messageTitle: t('messages.confirmUnsavedChangesTitle'),
      message: t('messages.confirmUnsavedChangesMessage'),
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });
    if (!answer) return;
    resetFormikValues(note);
    toggle();
  };

  const onSave = () => {
    submitForm();
  };

  return (
    <BaseModal
      title={modalName}
      open={isOpen}
      onCancel={onCancel}
      width={552}
      withFooter={[
        <BaseButton
          dataCy="cancel-button"
          key="cancel"
          type="secondary"
          label={t('controls.cancel')}
          width={112}
          onClick={onCancel}
          disabled={updateUnregisteredConversationNoteLoading}
        />,
        <BaseButton
          dataCy="save-button"
          key="save"
          type="primary"
          label={t('controls.save')}
          loading={updateUnregisteredConversationNoteLoading}
          width={112}
          onClick={onSave}
          disabled={values.note === null || values.note === ''}
        />,
      ]}
    >
      <BaseSpinWrapper spinning={updateUnregisteredConversationNoteLoading}>
        <BaseForm
          loading={updateUnregisteredConversationNoteLoading}
          formik={formik}
          plaintext={false}
          readonly={false}
          onSave={submitForm}
        >
          <BaseFormItem name="note" label={t('labels.note')}>
            <BaseTextArea />
          </BaseFormItem>
        </BaseForm>
      </BaseSpinWrapper>
      {Alert}
    </BaseModal>
  );
};

export default EditNoteModal;
